import { AiKnowledgeEntry } from './ai-assistant.types';

/**
 * Knowledge base chatbot — trích từ SOP.md (v2.8) và User guide (folder `User guide/`).
 *
 * Quy ước viết `questions` / `keywords`:
 * - `questions`: nhiều cách diễn đạt cho cùng một ý (câu hỏi FAQ, cách nói tắt, tên menu).
 * - `keywords`: ưu tiên **cụm từ** (2+ chữ) vì cụm được cộng điểm cao hơn từ đơn.
 *   Chỉ dùng từ đơn khi từ đó đủ đặc trưng (odo, camera, vendor, lolo…).
 * - Dấu tiếng Việt được chuẩn hóa khi so khớp, nên không cần thêm biến thể không dấu.
 *
 * Cập nhật khi SOP / User guide thay đổi.
 */
export const AI_ASSISTANT_KNOWLEDGE: AiKnowledgeEntry[] = [
    // ─── Đăng nhập & trợ giúp ───
    {
        id: 'auth-login',
        category: 'Đăng nhập',
        questions: [
            'Cách đăng nhập LTA Account',
            'Đăng nhập bằng tài khoản',
            'Làm sao đăng nhập I-TMS',
            'Quên mật khẩu thì làm sao',
            'Đổi mật khẩu ở đâu',
            'Không đăng nhập được',
            'Vào hệ thống bằng gì',
        ],
        keywords: [
            'đăng nhập',
            'dang nhap',
            'login',
            'lta account',
            'sso',
            'quên mật khẩu',
            'đổi mật khẩu',
            'mật khẩu',
            'tài khoản',
            'sign in',
            'đăng xuất',
        ],
        priority: 10,
        source: 'SOP §1 · màn auth',
        answer:
            '**Đăng nhập I-TMS:**\n' +
            '• **Đăng nhập LTA Account** — SSO doanh nghiệp (nút xanh trên màn login).\n' +
            '• **Đăng nhập bằng tài khoản** — nhập user và mật khẩu nội bộ.\n' +
            '• Quên mật khẩu → **Forgot password** trên màn đăng nhập, hệ thống gửi link đặt lại.\n' +
            'Sau đăng nhập, menu hiển thị theo **phân quyền** user (menu tải từ API, mỗi người thấy khác nhau).',
    },
    {
        id: 'user-guide',
        category: 'Trợ giúp',
        questions: [
            'Mở hướng dẫn sử dụng',
            'Trợ giúp trên màn hình',
            'User guide ở đâu',
            'Xem tài liệu hướng dẫn',
            'Có tài liệu nào không',
            'Hướng dẫn màn hình này',
        ],
        keywords: [
            'trợ giúp',
            'hướng dẫn sử dụng',
            'hướng dẫn',
            'user guide',
            'tài liệu',
            'help',
            'sop',
            'cẩm nang',
        ],
        priority: 8,
        source: 'User guide · navbar · user-guide.component',
        answer:
            '**Hướng dẫn theo màn hình:**\n' +
            '• Nút **Trợ giúp** trên thanh navbar → panel bên phải, nội dung theo **màn hình** bạn đang mở.\n' +
            '• Mỗi màn có hướng dẫn riêng (có ảnh chụp thao tác) — chatbot cũng tra cứu từ các hướng dẫn này.',
    },
    {
        id: 'terminology',
        category: 'Thuật ngữ',
        questions: [
            'FTL LTL FCL là gì',
            'Thuật ngữ I-TMS',
            'POD TRP là gì',
            'Các từ viết tắt trong hệ thống',
            'ORD là gì',
            'BK Bill là gì',
            'Loại V/C nghĩa là gì',
        ],
        keywords: [
            'thuật ngữ',
            'viết tắt',
            'ftl',
            'ltl',
            'fcl',
            'pod',
            'trp',
            'ord',
            'plhd',
            'adv',
            'loại v/c',
            'bk/bill',
        ],
        priority: 5,
        source: 'SOP §1.3',
        answer:
            '**Thuật ngữ thường gặp **\n' +
            '• **FTL** — nguyên chuyến xe tải · **LTL** — ghép hàng nhiều đơn/điểm · **FCL** — nguyên container (đầu kéo).\n' +
            '• **POD** — chứng nhận giao hàng · **Vendor** — xe thuê ngoài.\n' +
            '• **Loại V/C** — loại vận chuyển (FTL / LTL / FCL).\n' +
            '• Prefix mã: **ORD** đơn hàng · **TRP** chuyến · **BIL** bill · **ADV** tạm ứng · **PLHĐ** phụ lục hợp đồng.\n' +
            '• **BK/Bill** — số booking / vận đơn (đầu kéo) · **ETA/ETD** — giờ đến/đi dự kiến.',
    },

    // ─── Báo cáo & bản đồ đội xe ───
    {
        id: 'dashboard-kpi',
        category: 'Báo cáo',
        questions: [
            'Cách dùng bảng điều khiển báo cáo',
            'KPI báo cáo dashboard',
            'Bảng điều khiển I-TMS',
            'Xem doanh thu lợi nhuận ở đâu',
            'Báo cáo tổng quan ở đâu',
            'Số chuyến tỷ lệ hoàn thành xem ở đâu',
        ],
        keywords: [
            'bảng điều khiển',
            'báo cáo',
            'dashboard',
            'kpi',
            'insight',
            'doanh thu',
            'lợi nhuận',
            'tỷ lệ hoàn thành',
            'phương pháp tính',
        ],
        priority: 12,
        source: 'SOP §19 · /dashboard/i-tms',
        answer:
            '**Bảng điều khiển báo cáo **\n' +
            'Menu **Báo cáo** → **Bảng điều khiển I-TMS** (`/dashboard/i-tms`) → lọc **kỳ**, **doanh nghiệp**, **loại xe**.\n' +
            '• Nhóm tab: KPI Tổng quan, Phân tích Vận hành, Phân tích Tài chính, Top Rankings, Cảnh báo.\n' +
            '• Thẻ KPI: Doanh thu, Chi phí, Lợi nhuận, Số chuyến, Số đơn hàng, Tỷ lệ hoàn thành.\n' +
            '• Cảnh báo insight → click để cuộn tới khối liên quan.\n' +
            '• Một số khối có nút **Phương pháp tính** (vd. lệch KM GPS).',
    },
    {
        id: 'fleet-map',
        category: 'Bản đồ đội xe',
        questions: [
            'Bản đồ đội xe trên dashboard',
            'Cách dùng bản đồ đội xe',
            'Theo dõi và 1 xe khác gì',
            'Xem toàn bộ xe trên bản đồ',
            'Xem tất cả xe đang chạy ở đâu',
            'Bản đồ đội xe ở đâu',
            'So sánh vị trí nhiều xe',
            'Đo khoảng cách trên bản đồ',
            'Xem lịch sử GPS của xe',
        ],
        keywords: [
            'bản đồ đội xe',
            'fleet map',
            'toàn đội',
            'so sánh xe',
            'lịch sử gps',
            'đo khoảng cách',
            'bán kính',
            'theo dõi xe',
            'signalr',
            'gpsmap',
        ],
        priority: 18,
        source: 'SOP §19.10 · A.5.4 · fleet-map-help',
        answer:
            '**Bản đồ đội xe:**\n' +
            '**Báo cáo** → **Bảng điều khiển I-TMS** → nút **Bản đồ đội xe** trên header.\n\n' +
            '• GPS realtime qua SignalR **GPSMap**; lọc bằng chip KPI (có GPS, đang chạy chuyến, điểm chờ…).\n' +
            '• Lộ trình chuyến (OSRM), camera xe, lịch sử GPS 2h–24h.\n' +
            '• **Theo dõi** = bản đồ tự pan theo xe đang chọn (tự tắt khi bạn kéo map).\n' +
            '• **1 xe** = chỉ hiện marker xe đang chọn, không tự pan.\n' +
            '• **So sánh** tối đa 3 xe · **Đo** khoảng cách hoặc vùng bán kính.\n' +
            '• Bộ lọc doanh nghiệp / loại xe / xe nhà được đồng bộ từ dashboard.',
    },

    // ─── Đơn hàng ───
    {
        id: 'order-truck',
        category: 'Đơn hàng',
        questions: [
            'Tạo đơn hàng tải',
            'Đơn hàng xe tải và đơn hàng tải khác nhau không',
            'Gửi điều phối đơn hàng',
            'Tại sao không gửi được điều phối',
            'Mã đơn hàng lấy ở đâu',
            'Thêm mới đơn hàng ở đâu',
            'Tìm một đơn hàng cụ thể',
            'Cột điểm giao hiển thị gì',
            'Sau khi tạo chuyến xem ở đâu',
        ],
        keywords: [
            'đơn hàng tải',
            'đơn hàng xe tải',
            'gửi điều phối',
            'tạo đơn hàng',
            'mã đơn hàng',
            'shipment',
            'điểm giao hàng',
            'tổng pallet',
            'file đính kèm',
        ],
        priority: 14,
        source: 'SOP §8.1 · User guide Đơn hàng tải (form 89)',
        answer:
            '**Đơn hàng tải:**\n' +
            '**Thêm mới** → Khách hàng, Loại vận chuyển (FTL/LTL), Điểm nhận hàng, ETD/ETA → lưới con **Danh sách điểm giao** và **Danh sách sản phẩm** → **Lưu**.\n' +
            'Mã đơn hệ thống tự sinh dạng `ORD.yymmddxxx` sau khi lưu lần đầu.\n\n' +
            '**Không gửi được điều phối?** Phải **tick checkbox** dòng đơn và đơn phải ở trạng thái **Đã tiếp nhận**.\n' +
            'Tab trạng thái: Tất cả · Nháp · Chờ tiếp nhận · Đã tiếp nhận · Từ chối · Đã tạo chuyến · Đang giao · Đã giao · Hoàn tất · Đã hủy.\n' +
            'Sau khi điều phối, xem chuyến tại **Vận hành › Điều phối › Chuyến xe tải**.',
    },
    {
        id: 'order-tractor',
        category: 'Đơn hàng',
        questions: [
            'Đơn hàng đầu kéo',
            'Tạo đơn hàng container',
            'Import số container ở đâu',
            'Tại sao đơn đầu kéo có trạng thái Chứng từ',
            'Đơn hàng đầu kéo khác đơn hàng tải thế nào',
            'Nhập danh sách container hàng loạt',
        ],
        keywords: [
            'đơn hàng đầu kéo',
            'đơn hàng xe đầu kéo',
            'import số container',
            'danh sách container',
            'hãng tàu',
            'cutoff',
            'hạn trả rỗng',
            'ngày lấy cont',
            'loại cont',
        ],
        priority: 15,
        source: 'SOP §9.2 · User guide Đơn hàng đầu kéo (form 90)',
        answer:
            '**Đơn hàng đầu kéo** (Vận hành › Đơn đặt hàng › **Đơn hàng đầu kéo**, form `90`):\n' +
            'Form nhập: Dịch vụ (Nhập/Xuất/Chuyển rỗng/Tái xuất/Vận chuyển nội địa), Hãng tàu, **BK/Bill**, Loại Cont, Số lượng, **Ngày lấy Cont**, **Cutoff/DEM**, **Hạn trả rỗng**, Điểm lấy / Điểm đóng-rút / Điểm hạ.\n\n' +
            '• **Import Số Container:** trong form → khối **Danh sách container** → **Import Số Container** (dùng **File template**).\n' +
            '• Trạng thái **Chứng từ** = giai đoạn xử lý hồ sơ/booking trước hoặc trong khi vận hành (đầu kéo có thêm trạng thái này so với đơn tải).\n' +
            '• Không gửi được điều phối → tick checkbox + đơn phải **Đã tiếp nhận**.',
    },

    // ─── Điều phối tải ───
    {
        id: 'coord-truck-flow',
        category: 'Điều phối tải',
        questions: [
            'Hướng dẫn điều phối chuyến xe',
            'Điều phối một chuyến xe tải',
            'Quy trình tạo chuyến tải',
            'Làm sao gửi chuyến điều phối',
            'Điều phối xe tải làm thế nào',
            'Điều phối tải và điều phối xe tải khác nhau không',
            'Cách lập chuyến cho xe',
            'Làm sao chỉ điều phối một đơn',
            'Tiếp nhận đơn trên màn điều phối',
        ],
        keywords: [
            'điều phối tải',
            'điều phối xe tải',
            'điều phối',
            'lập chuyến',
            'gửi chuyến',
            'tạo chuyến',
            'coordination',
            'đã chọn',
            'nhà vận tải',
            'tiếp nhận đơn hàng',
        ],
        priority: 14,
        source: 'SOP §8.2 · User guide Điều phối tải (form 94)',
        answer:
            '**Điều phối tải:**\n' +
            'Màn 3 cột: **Đơn hàng** | **Giao hàng / Đã chọn** | **Bản đồ + Form thông tin chuyến**.\n\n' +
            '**Quy trình:** Chọn điểm giao (cột giữa) → điểm vào **Đã chọn** → **Vẽ Route** → chọn **Nhà vận tải** (Xe nhà / Vendor) → điền Thời gian bắt đầu, Xe, Tài xế → **Gửi**.\n' +
            'Hệ thống sinh mã **TRP**, chuyến ở trạng thái **Chờ nhận lệnh**.\n\n' +
            '• Nút **Tiếp nhận** (cột trái) mở popup tiếp nhận đơn ngay tại màn điều phối.\n' +
            '• Tab **Chuyến đã gửi** để tra cứu chuyến vừa gửi trong ngày / 7 ngày.',
    },
    {
        id: 'coord-plan',
        category: 'Điều phối tải',
        questions: [
            'Kế hoạch đề xuất điều phối tải',
            'Kế hoạch đề xuất làm gì',
            'Tối ưu tạo chuyến tự động',
            'Hệ thống tự gợi ý chuyến',
            'Kế hoạch đề xuất khác Kế hoạch thế nào',
            'Không bấm được kế hoạch đề xuất',
        ],
        keywords: [
            'kế hoạch đề xuất',
            'đề xuất chuyến',
            'tối ưu tạo chuyến',
            'optimize router',
            'gợi ý phân bổ xe',
            'osrm',
        ],
        priority: 17,
        source: 'SOP §8.2.1 · §8.2.2',
        answer:
            '**Kế hoạch đề xuất:**\n' +
            'Wizard tối ưu từ các điểm đã chọn — điều kiện: **≥ 3 điểm**, **cùng điểm lấy hàng**, **không phải đơn trung chuyển**.\n' +
            'Hệ thống gợi ý phân bổ xe / tài xế + lộ trình (API **OptimizeRouter**, tuyến vẽ bằng OSRM). Sau khi xác nhận → tạo chuyến trạng thái **Chờ nhận lệnh**.\n\n' +
            '**Khác nút “Kế hoạch”** trên bản đồ *Kế hoạch* chỉ để **xem báo cáo**, không tạo chuyến.\n' +
            'Đầu kéo **không có** Kế hoạch đề xuất.',
    },
    {
        id: 'coord-route',
        category: 'Điều phối tải',
        questions: [
            'Vẽ Route điều phối',
            'Tối ưu Route ở đâu',
            'Xóa Route',
            'Vẽ Route và Xóa Route khác nhau thế nào',
            'Không hiện tuyến đường trên bản đồ',
            'Sắp xếp thứ tự điểm giao',
        ],
        keywords: [
            'vẽ route',
            'xóa route',
            'tối ưu route',
            'lộ trình',
            'tuyến đường',
            'polyline',
            'thứ tự điểm',
        ],
        priority: 15,
        source: 'SOP §8.2.4 · User guide Điều phối tải §11',
        answer:
            '**Vẽ / tối ưu route:**\n' +
            '• **Vẽ Route** — hiển thị tuyến OSRM trên bản đồ theo các điểm trong **Đã chọn** (map báo *Đang vẽ…* → *Vẽ Route: n điểm*).\n' +
            '• **Xóa Route** — gỡ tuyến để vẽ lại sau khi đổi điểm.\n' +
            '• **Tối ưu Route** — chỉ bật khi nhiều điểm / theo cấu hình doanh nghiệp.\n' +
            '• Đổi thứ tự điểm bằng nút **di chuyển lên / xuống** trong danh sách **Đã chọn** rồi vẽ lại.',
    },
    {
        id: 'coord-fleet-gps',
        category: 'Điều phối',
        questions: [
            'Fleet GPS trên điều phối tải',
            'Bật Fleet GPS',
            'Xem xe gần khu vực khi lập chuyến',
            'Ẩn fleet GPS',
            'Fleet GPS khác bản đồ đội xe thế nào',
        ],
        keywords: ['fleet gps', 'ẩn fleet', 'gis fleet', 'marker xe', 'vị trí xe realtime'],
        priority: 17,
        source: 'SOP §8.2.5 · §9.3.1',
        answer:
            '**Fleet GPS:**\n' +
            'Trên bản đồ **Điều phối tải / đầu kéo** → nút **Fleet GPS** (bấm lại là **Ẩn fleet GPS**).\n' +
            'Hiện vị trí realtime của xe trong đội — giúp chọn xe đang ở gần khu vực lấy hàng khi lập chuyến.\n\n' +
            'Khác **Bản đồ đội xe** trên dashboard: bản đồ đội xe xem cả đội tải + đầu kéo và có nhiều công cụ hơn (so sánh, đo, lịch sử GPS, camera).',
    },
    {
        id: 'coord-split',
        category: 'Điều phối tải',
        questions: [
            'Tách đơn hàng',
            'Gộp đơn hàng',
            'Tách điểm giao hàng',
            'Tách sản phẩm',
            'Tách đơn khi nào',
            'Chia đơn cho nhiều xe',
        ],
        keywords: [
            'tách đơn',
            'gộp đơn',
            'tách điểm',
            'gộp điểm',
            'tách sản phẩm',
            'số lượng cần tách',
            'chia đơn',
        ],
        priority: 16,
        source: 'SOP §8.2.3',
        answer:
            '**Tách / Gộp:**\n' +
            '• **Tách đơn hàng / Tách điểm giao hàng / Tách sản phẩm** — chia hàng LTL lên nhiều chuyến; modal có ô **Số lượng cần tách**.\n' +
            '• **Gộp đơn hàng / Gộp điểm giao hàng** — khi điều kiện nghiệp vụ cho phép.\n' +
            'Thao tác từ thẻ đơn ở cột trái hoặc lưới **Giao hàng** ở cột giữa.',
    },
    {
        id: 'coord-send-disabled',
        category: 'Điều phối tải',
        questions: [
            'Tại sao nút Gửi bị mờ',
            'Gửi chuyến không được',
            'Nút Gửi disabled',
            'Không bấm được nút Gửi',
            'Bấm Gửi không có gì xảy ra',
        ],
        keywords: [
            'nút gửi bị mờ',
            'gửi bị mờ',
            'gửi disabled',
            'không gửi được',
            'không bấm được gửi',
            'đặt lại form',
        ],
        priority: 19,
        source: 'User guide Điều phối tải — FAQ',
        answer:
            '**Nút Gửi bị mờ** thường do:\n' +
            '• Chưa có điểm nào trong danh sách **Đã chọn**.\n' +
            '• Thiếu trường bắt buộc trên form chuyến: **Thời gian bắt đầu thực hiện**, **Nhà vận tải**, và **Xe / Tài xế** (xe nhà) hoặc **Số xe / Tên tài xế** (vendor).\n' +
            '• Đơn chưa ở trạng thái **Đã tiếp nhận**.\n' +
            'Nên **Vẽ Route** trước khi Gửi để xác nhận lộ trình. Muốn nhập lại từ đầu → **Đặt lại form**.',
    },
    {
        id: 'coord-vendor',
        category: 'Điều phối tải',
        questions: [
            'Xe nhà và Vendor khác gì',
            'Form vendor điều phối',
            'Điều phối xe thuê ngoài',
            'Nhập CCCD tài xế vendor',
            'Chọn nhà vận tải nào',
        ],
        keywords: [
            'xe nhà',
            'vendor',
            'thuê ngoài',
            'giá thuê',
            'cccd mặt trước',
            'nhà vận tải',
            'số xe',
            'tên tài xế',
        ],
        priority: 16,
        source: 'User guide Điều phối tải §6.3',
        answer:
            '**Xe nhà vs Vendor** — chọn ở combobox **Nhà vận tải**, form sẽ đổi theo:\n' +
            '• **Xe nhà:** chọn **Xe**, **Tài xế**, **Giao hàng**, **Giao hàng 2**, **Settle** (đầu kéo thêm **Mooc**, **Phụ xế**).\n' +
            '• **Vendor:** nhập tay **Số xe**, **Tên tài xế**, upload **CCCD mặt trước / mặt sau**, **Hợp đồng**, **Giá thuê**.',
    },
    {
        id: 'dispatched-trips-tab',
        category: 'Điều phối',
        questions: [
            'Tab chuyến đã gửi',
            'Xem lại chuyến vừa gửi',
            'Chuyến đã gửi ở đâu',
            'Tra cứu chuyến vừa điều phối',
        ],
        keywords: ['chuyến đã gửi', 'chuyến vừa gửi', 'dispatched'],
        priority: 15,
        source: 'SOP §8.2.6',
        answer:
            '**Tab Chuyến đã gửi:**\n' +
            'Nằm ở cột trái màn **Điều phối tải / đầu kéo** — tra cứu chuyến vừa **Gửi** (lọc *Hôm nay* / *7 ngày*).\n' +
            'Tự làm mới sau khi Gửi thành công. Chỉ **xem / lọc**; muốn thao tác đầy đủ thì vào **Chuyến xe tải** hoặc **Giám sát**.',
    },

    // ─── Điều phối đầu kéo ───
    {
        id: 'coord-tractor',
        category: 'Điều phối đầu kéo',
        questions: [
            'Điều phối xe đầu kéo FCL',
            'Điều phối đầu kéo làm thế nào',
            'Điều phối container',
            'Điều phối đầu kéo có kế hoạch đề xuất không',
            'Điều phối đầu kéo khác điều phối tải thế nào',
        ],
        keywords: [
            'điều phối đầu kéo',
            'điều phối xe đầu kéo',
            'điều phối container',
            'coordination tractor',
            'mooc',
            'số container 2',
        ],
        priority: 15,
        source: 'SOP §9.3 · User guide Điều phối đầu kéo (form 96)',
        answer:
            '**Điều phối đầu kéo:**\n' +
            'Cách dùng giống điều phối tải nhưng:\n' +
            '• **Không có Kế hoạch đề xuất** và thường không có Tối ưu Route.\n' +
            '• Điểm theo container: **Điểm lấy** → **Điểm đóng/rút** → **Điểm hạ**.\n' +
            '• Form chuyến thêm **Mooc**, **Phụ xế**, **Số Container**, **Số Container 2**.\n' +
            '• Lọc đơn theo **Mã ĐH / BK / Bill**, **Dịch vụ**, **Loại Container**.\n' +
            '• **Fleet GPS** dùng như bên tải.',
    },

    // ─── Chuyến xe (danh sách) ───
    {
        id: 'trip-list-truck',
        category: 'Chuyến xe',
        questions: [
            'Chuyến xe tải khác điều phối tải thế nào',
            'Danh sách chuyến xe tải',
            'Xem chuyến sau khi điều phối ở đâu',
            'Tạo chuyến mới ở đâu',
            'Soạn hàng dùng để làm gì',
            'Nhận lệnh từ chối lệnh trên web',
            'Hủy chuyến ở đâu',
            'Xuất file soạn hàng',
        ],
        keywords: [
            'chuyến xe tải',
            'danh sách chuyến',
            'soạn hàng',
            'nhận lệnh',
            'từ chối lệnh',
            'hủy lệnh',
            'xuất file soạn hàng',
            'mã khác',
        ],
        priority: 15,
        source: 'User guide Chuyến xe tải (form 95) · SOP §8.3',
        answer:
            '**Chuyến xe tải** (Vận hành › Điều phối › **Chuyến xe tải**, form `95`):\n' +
            'Màn **danh sách** để tra cứu chuyến đã tạo — **không tạo chuyến mới ở đây**, chuyến sinh ra từ màn **Điều phối tải**.\n\n' +
            '• Toolbar: **Soạn hàng**, Tìm, Tải lại, Cột.\n' +
            '• Chuột phải một dòng: **Xem chi tiết**, **Hủy**, **Nhận lệnh**, **Từ chối**, **Xuất file soạn hàng** (menu đổi theo trạng thái).\n' +
            '• Tab trạng thái: Tất cả · Nháp · Chờ nhận lệnh · Đã nhận lệnh · Từ chối lệnh · Đang vận chuyển · Hoàn thành · Hủy lệnh · Thất bại.\n' +
            'Lưu ý **Hoàn thành** (chuyến) khác **Hoàn tất** (đơn hàng).',
    },
    {
        id: 'trip-list-tractor',
        category: 'Chuyến xe',
        questions: [
            'Chuyến xe đầu kéo ở đâu',
            'Danh sách chuyến đầu kéo',
            'Tại sao chuyến đầu kéo không có Soạn hàng',
            'Bật cột Điểm lấy Mooc ở đâu',
            'ORD và DDH là gì',
        ],
        keywords: ['chuyến xe đầu kéo', 'chuyến đầu kéo', 'cặp cổ', 'điểm đóng/rút', 'điểm hạ'],
        priority: 15,
        source: 'User guide Chuyến xe đầu kéo (form 97) · SOP §9.4',
        answer:
            '**Chuyến xe đầu kéo** (Vận hành › Điều phối › **Chuyến xe đầu kéo**, form `97`):\n' +
            'Danh sách tra cứu chuyến đầu kéo. Toolbar chỉ có **Tìm / Tải lại / Cột** — **không có Soạn hàng** và không có Thêm mới (chuyến sinh từ **Điều phối đầu kéo**).\n' +
            'Cột đặc thù: Loại V/C, Dịch vụ, ETA, ETD, Hãng tàu, Container, BK/Bill, Ngày lấy Cont, Cutoff/DEM, **Điểm lấy / Điểm đóng-rút / Điểm hạ**, Cặp cổ, Mooc, Phụ xế.\n' +
            'Cột nào không thấy → nút **Cột** → tick → **Lưu cấu hình**.',
    },

    // ─── Giám sát ───
    {
        id: 'oversee-layout',
        category: 'Giám sát',
        questions: [
            'Giám sát vận hành và GPS',
            'Bố cục màn giám sát',
            'Giám sát xe tải dùng thế nào',
            'Theo dõi chuyến đang chạy',
            'Xem vị trí xe ở đâu',
            'Màn giám sát có gì',
        ],
        keywords: [
            'giám sát xe tải',
            'giám sát vận hành',
            'giám sát',
            'over-see',
            'hiển thị tất cả xe',
            'hiển thị sự cố',
            'phóng to bản đồ',
            'tắt gom nhóm xe',
        ],
        priority: 15,
        source: 'SOP §10 · User guide Giám sát xe tải (form 99)',
        answer:
            '**Giám sát vận hành:**\n' +
            'Màn **4 vùng:** 1) Danh sách chuyến · 2) Bản đồ GPS · 3) Panel thông tin chuyến (trái) · 4) Panel thông tin điểm (phải).\n\n' +
            '• Toolbar lưới: **Hiển thị chuyến đang xử lý**, **Hiển thị sự cố**, **Hiển thị tất cả xe**, **Lọc dữ liệu**, **Tải lại**, **Đặt lại lưới**.\n' +
            '• Icon cột **Thao tác**: Xem chi tiết (mắt), Chứng từ, Khóa chuyến.\n' +
            '• Bản đồ: **Phóng to bản đồ**, **Tắt gom nhóm xe**, Zoom +/−.',
    },
    {
        id: 'oversee-no-data',
        category: 'Giám sát',
        questions: [
            'Không có dữ liệu chuyến giám sát',
            'Lưới trống giám sát',
            'Vì sao lưới báo không có dữ liệu chuyến',
            'Không thấy chuyến nào trên giám sát',
            'Màn giám sát không hiện chuyến nào',
            'Danh sách chuyến bị trống',
        ],
        keywords: [
            'không có dữ liệu chuyến',
            'không có dữ liệu',
            'lưới trống',
            'không thấy chuyến',
            'không hiện chuyến',
            'xóa bộ lọc',
        ],
        priority: 19,
        source: 'User guide Giám sát xe tải — FAQ',
        answer:
            '**Lưới không có chuyến** — thử theo thứ tự:\n' +
            '1) Bật **Hiển thị tất cả xe** (đang lọc *chuyến đang xử lý* nên chuyến cũ bị ẩn).\n' +
            '2) Mở **Lọc dữ liệu** → **Xóa bộ lọc** hoặc mở rộng **Ngày chuyến**.\n' +
            '3) Nhấn **Tải lại**; nếu lưới lệch cột thì **Đặt lại lưới**.\n' +
            '4) Kiểm tra bộ lọc **Doanh nghiệp** — bạn chỉ thấy dữ liệu thuộc quyền của mình.',
    },
    {
        id: 'oversee-trip-status',
        category: 'Giám sát',
        questions: [
            'Trạng thái giám sát chuyến',
            'Bắt đầu chuyến giám sát',
            'Hoàn tất chuyến',
            'Xác nhận chuyến khi nào',
            'Hủy xác nhận chuyến',
            'Hoàn tất chuyến khác Đã giao hàng thế nào',
            'Các trạng thái chuyến trên giám sát',
        ],
        keywords: [
            'bắt đầu chuyến',
            'hoàn tất chuyến',
            'xác nhận chuyến',
            'hủy xác nhận',
            'đã giao hàng',
            'đã xác nhận',
            'chờ vận chuyển',
            'đang vận chuyển',
            'thời gian chuyến',
        ],
        priority: 16,
        source: 'SOP §10.2 · §10.4 · §17.3',
        answer:
            '**Trạng thái giám sát chuyến:**\n' +
            'Chờ vận chuyển → Đang vận chuyển → Đã giao hàng → **Đã xác nhận** (kèm nhánh Chờ xác nhận sự cố / Đang xử lý sự cố / Thất bại / Đã hủy).\n\n' +
            '• **Bắt đầu chuyến** — nhập **ODO** + ảnh (có thể OCR) → chuyến sang *Đang vận chuyển*.\n' +
            '• **Hoàn tất chuyến** — **thao tác** kết thúc hành trình; khác **Đã giao hàng** là **trạng thái nghiệp vụ**.\n' +
            '• **Xác nhận chuyến** — khi mọi điểm đã xong và số liệu đúng; sau đó mới **Hoàn tất chứng từ**.\n' +
            '• **Hủy xác nhận** — dùng khi cần sửa lại, chỉ được phép trước khi khóa chi phí.',
    },
    {
        id: 'oversee-point',
        category: 'Giám sát',
        questions: [
            'Xác nhận giao nhận tại điểm',
            'Đã giao nhận tại điểm',
            'Thêm chứng từ chi phí tại điểm',
            'Đã giao nhận có bắt buộc ảnh không',
            'Upload ảnh POD',
            'Báo điểm thất bại',
            'Panel điểm bị trống',
            'Thêm sự cố ở đâu',
        ],
        keywords: [
            'đã giao/nhận',
            'đã giao nhận',
            'điểm vận chuyển',
            'thêm chứng từ',
            'chi phí phát sinh',
            'thêm sự cố',
            'số lượng thực tế',
            'lý do thất bại',
            'hình ảnh giao nhận',
            'ảnh pod',
        ],
        priority: 17,
        source: 'SOP §10.3 · §10.6 · User guide Giám sát §13–16',
        answer:
            '**Thao tác tại điểm trên Giám sát:**\n' +
            '1) Chọn chuyến → tab **Điểm vận chuyển** → chọn **một điểm** (panel phải mới có dữ liệu, nếu chưa chọn điểm thì panel trống).\n' +
            '2) Nút **Đã giao/nhận** — modal bắt buộc **hình ảnh giao/nhận hàng** + **số lượng thực tế** theo SKU.\n' +
            '3) Nút **Thất bại** — phải nhập **Lý do thất bại**.\n' +
            '4) Tab **Chứng từ** / **Chi phí phát sinh** trên panel điểm → nút **+ Thêm**.\n' +
            '5) Sự cố: tab **Sự cố** trên panel chuyến → **Thêm sự cố**.\n\n' +
            'Trạng thái điểm: Chờ vận chuyển → Đang đến → Đã đến → Đã giao/nhận (hoặc Thất bại). GPS cảnh báo nếu xác nhận cách điểm > 200m.',
    },
    {
        id: 'oversee-odo',
        category: 'Giám sát',
        questions: [
            'Quy tắc ODO',
            'ODO kết thúc có quy tắc gì',
            'OCR ODO container',
            'Nhập số ODO ở đâu',
            'Báo lỗi ODO kết thúc nhỏ hơn',
        ],
        keywords: ['odo', 'ocr', 'công tơ mét', 'số odo', 'hình ảnh odo', 'khóa màn hình'],
        priority: 17,
        source: 'SOP §10.5 · §10.10',
        answer:
            '**ODO & OCR:**\n' +
            '• **ODO kết thúc phải ≥ ODO bắt đầu** chuyến — sai điều kiện này hệ thống chặn.\n' +
            '• Modal **Bắt đầu chuyến** / **Hoàn tất chuyến**: nhập **Số odo** + **Hình ảnh Odo**; OCR có thể cảnh báo khi số đọc được lệch với số nhập tay.\n' +
            '• Đầu kéo: OCR **số container** khi doanh nghiệp bật cấu hình **IsOCR**.\n' +
            '• Trong lúc upload/OCR, màn bị khóa chờ để tránh bấm trùng — đợi xử lý xong.',
    },
    {
        id: 'oversee-camera',
        category: 'Giám sát',
        questions: [
            'Xem camera xe trên giám sát',
            'Camera CTMS',
            'Video trực tiếp xe',
            'Không xem được camera',
            'Xem hình ảnh trong cabin',
        ],
        keywords: ['camera', 'ctms', 'video trực tiếp', 'live stream', 'ch1', 'ch2'],
        priority: 17,
        source: 'SOP §10.9',
        answer:
            '**Camera xe:**\n' +
            'Mở từ **Giám sát** hoặc **Bản đồ đội xe** — stream HTTP/iframe hoặc **CTMS live** (kênh **CH1 / CH2**).\n' +
            'Không xem được thường do: thiết bị chưa gắn camera, cấu hình doanh nghiệp chưa bật, hoặc phiên **CTMS** chưa đăng nhập.',
    },
    {
        id: 'oversee-tractor-extra',
        category: 'Giám sát đầu kéo',
        questions: [
            'Phí LoLo ở đâu',
            'Kiểm hóa đầu kéo',
            'Giám sát FCL',
            'Thay đổi điểm giao nhận',
            'Giám sát đầu kéo khác giám sát tải thế nào',
            'Nhập số seal ở đâu',
        ],
        keywords: [
            'phí lolo',
            'lolo',
            'kiểm hóa',
            'thay đổi điểm',
            'số seal',
            'hình ảnh container',
            'giám sát đầu kéo',
            'giám sát xe đầu kéo',
        ],
        priority: 17,
        source: 'SOP §9.5 · User guide Giám sát xe đầu kéo (form 100)',
        answer:
            '**Giám sát đầu kéo — điểm khác so với tải:**\n' +
            '• Panel điểm có thêm tab **Phí LoLo** (thêm bằng icon **+**).\n' +
            '• Nút **Kiểm hóa** ở tab Điểm vận chuyển — chọn **Tại điểm** hoặc **Điểm khác**.\n' +
            '• Icon **ghim vàng** trên dòng điểm = **Thay đổi điểm giao/nhận**.\n' +
            '• Modal hoàn tất điểm nhập thêm **Số KM**, **Số Seal**, **Hình ảnh Container**, **Hình ảnh Seal**.\n' +
            '• Cột đặc thù: Loại vận chuyển, Dịch vụ (Nhập / Xuất / Chuyển rỗng), Nhận, Đóng/rút, Hạ, Kiểm hóa.',
    },

    // ─── Mobile ───
    {
        id: 'mobile-v2',
        category: 'Mobile',
        questions: [
            'Nhận lệnh trên mobile tài xế',
            'Ứng dụng mobile v2',
            'Tài xế dùng app thế nào',
            'Tài xế từ chối lệnh',
            'App tài xế có chức năng gì',
            'Xem lương trên điện thoại',
        ],
        keywords: [
            'mobile',
            'tài xế',
            'app tài xế',
            'nhận lệnh',
            'từ chối lệnh',
            'chuyến của tôi',
            'transport truck',
            'soạn hàng mobile',
            'nâng hàng',
        ],
        priority: 14,
        source: 'SOP §22 · routes /i-tms/v2/**',
        answer:
            '**Mobile tài xế v2:**\n' +
            'Hub **Tổng quan tài xế** với các module: **Vận chuyển** (tải / đầu kéo), **Nhiên liệu**, **Sửa chữa**, **Lương**, **Soạn hàng**, **Nâng hàng**, **Ý kiến**, **Hồ sơ nhân sự**, **Thông báo**.\n\n' +
            '• **Nhận lệnh / Từ chối lệnh** thực hiện trong module **Vận chuyển**.\n' +
            '• Deep-link `?statusId=` mở thẳng tab chờ nhận / đang chạy.\n' +
            '• Ảnh POD chụp trên app có **watermark thời gian + GPS**.\n' +
            '• Thanh dưới: Trang chủ · Thông báo · Lương · Tài khoản; nút giữa là **Chuyến của tôi**.',
    },
    {
        id: 'mobile-fuel',
        category: 'Mobile',
        questions: [
            'Cấp nhiên liệu trên mobile',
            'Nhiên liệu mobile v2',
            'Tài xế đổ dầu ghi nhận thế nào',
            'Phiếu cấp nhiên liệu trên app',
        ],
        keywords: ['nhiên liệu mobile', 'cấp nhiên liệu mobile', 'phiếu cấp nl', 'fuel v2'],
        priority: 17,
        source: 'SOP §7.6 · §22.4',
        answer:
            '**Nhiên liệu trên mobile:**\n' +
            'Module **Nhiên liệu** (`/i-tms/v2/fuel`) — tab theo trạng thái phiếu (**Chờ cấp** / **Đã cấp** / **Đã xác nhận**).\n' +
            'Tài xế chụp ảnh và ghi nhận số lượng cấp thực tế; dữ liệu đẩy về màn **Danh sách cấp nhiên liệu** trên web để duyệt và chốt.',
    },

    // ─── Chứng từ ───
    {
        id: 'documents',
        category: 'Chứng từ',
        questions: [
            'Hoàn tất chứng từ chuyến',
            'Quy trình chứng từ',
            'Hủy hoàn tất chứng từ',
            'Hoàn tất CT khác Đã xác nhận thế nào',
            'Bật Hoàn tất CT khi nào',
            'Không thấy chuyến vừa upload chứng từ',
            'Upload chứng từ ở đâu',
        ],
        keywords: [
            'hoàn tất chứng từ',
            'hủy hoàn tất',
            'hoàn tất ct',
            'chứng từ xe tải',
            'chứng từ đầu kéo',
            'chứng từ',
            'sl chứng từ',
        ],
        priority: 15,
        source: 'SOP §11 · User guide Chứng từ (form 103 / 104)',
        answer:
            '**Chứng từ:**\n' +
            'Sau khi giám sát **Xác nhận chuyến** → chọn chuyến → **Hoàn tất chứng từ**. Cần bổ sung/sửa thì **Hủy hoàn tất chứng từ**.\n\n' +
            '• Tab lọc: **Tất cả** / **Chưa hoàn tất** / **Đã hoàn tất**; cột **Hoàn tất CT** là toggle.\n' +
            '• **Hoàn tất CT** ≠ **Đã xác nhận**: *Đã xác nhận* là trạng thái vận hành của chuyến, *Hoàn tất CT* là cờ hồ sơ.\n' +
            '• Không thấy chuyến vừa upload → nhấn **Tải lại** và kiểm tra đang đứng ở tab nào.\n' +
            '• Thứ tự nghiệp vụ: Xác nhận giám sát → **Hoàn tất CT** → Khóa chi phí → Đối chiếu → Chốt bill.',
    },

    // ─── Chi phí chuyến ───
    {
        id: 'trip-cost',
        category: 'Chi phí chuyến',
        questions: [
            'Khóa chi phí chuyến',
            'Mở khóa dữ liệu khi nào',
            'Khóa dữ liệu khi nào',
            'Nút Khóa không chạy',
            'Đã khóa còn sửa chi phí được không',
            'Cột Phát sinh 0 0 2 nghĩa là gì',
            'Chi phí chuyến khác chuyến xe tải thế nào',
            'Sau khi khóa thì làm gì',
        ],
        keywords: [
            'khóa chi phí',
            'khóa dữ liệu',
            'mở khóa dữ liệu',
            'mở khóa',
            'chi phí chuyến',
            'không phát sinh',
            'chưa khóa',
            'đã khóa',
            'settle',
            'trung chuyển',
        ],
        priority: 17,
        source: 'SOP §12 · User guide Chi phí chuyến (form 109 / 110)',
        answer:
            '**Chi phí chuyến:**\n' +
            'Màn rà soát **tiền** của chuyến rồi **khóa** lại (khác màn *Chuyến xe tải* vốn theo dõi trạng thái vận chuyển).\n\n' +
            '• **Khóa dữ liệu** khi: chi phí phát sinh đã duyệt xong **và** chuyến đã **Hoàn tất CT**.\n' +
            '• **Mở khóa dữ liệu** khi: đã khóa **và** **chưa xác nhận đối chiếu**.\n' +
            '• Bấm Khóa không chạy → chưa **tick checkbox** dòng, hoặc chưa đủ điều kiện trên.\n' +
            '• Cột **Phát sinh** `0 / 0 / 2` = ba chỉ số tóm tắt khoản phát sinh → mở panel tab **Chi phí phát sinh** để xử lý.\n' +
            '• Sau khóa → **Đối chiếu** → **Chốt bill**.',
    },

    // ─── Đối chiếu & chốt bill ───
    {
        id: 'reconcile',
        category: 'Đối chiếu',
        questions: [
            'Đối chiếu cước chuyến',
            'Xác nhận đối chiếu khi nào',
            'Hủy xác nhận đối chiếu',
            'Đối chiếu đầu kéo',
            'Chưa có HĐ PLHĐ xử lý thế nào',
            'Tổng tiền bằng 0',
            'Nút Xác nhận không chạy',
            'Đối chiếu tải khác chi phí chuyến thế nào',
        ],
        keywords: [
            'đối chiếu',
            'đối chiếu tải',
            'đối chiếu đầu kéo',
            'xác nhận đối chiếu',
            'hủy xác nhận đối chiếu',
            'chưa có hđ',
            'plhđ',
            'tiền cước',
            'tổng tiền',
            'xem doanh thu',
        ],
        priority: 17,
        source: 'SOP §13 · User guide Đối chiếu (form 114 / 115)',
        answer:
            '**Đối chiếu cước:**\n' +
            'Sau khi **khóa chi phí**, hệ thống map chuyến với **bảng cước** trong hợp đồng/phụ lục rồi tính **Tiền cước** + **Phát sinh** = **Tổng tiền**.\n\n' +
            '• Tab: Tất cả · **Chưa có HĐ/PLHĐ** · Chưa tính · Chưa xác nhận · Đã xác nhận · Chốt bill.\n' +
            '• **Chưa có HĐ/PLHĐ** → khách chưa gắn hợp đồng/phụ lục còn hiệu lực; bổ sung bên **Quản lý hợp đồng** rồi tính lại.\n' +
            '• **Tổng tiền = 0** → thường do chưa map được bảng cước hoặc thiếu tham số tính cước.\n' +
            '• **Xác nhận** cần tick dòng; **Hủy xác nhận** chỉ được khi **chưa chốt bill**.\n' +
            '• Đầu kéo có thêm cột **Phí LOLO**, Số Container, BK/Bill.',
    },
    {
        id: 'bill',
        category: 'Chốt bill',
        questions: [
            'Chốt bill làm thế nào',
            'Tạo bill mới',
            'Tạo bill cho khách hàng',
            'Chốt Bill đầu kéo',
            'Thêm mới bill không ra chuyến',
            'Loại thời gian chọn gì',
            'Tổng VAT bằng 0',
            'Đã xuất hóa đơn làm gì',
            'Tổng LOLO là gì',
        ],
        keywords: [
            'chốt bill',
            'bill mới tạo',
            'đã chốt bill',
            'đã xuất hóa đơn',
            'cập nhật hóa đơn',
            'tổng cước',
            'tổng vat',
            'tổng lolo',
            'mã bill',
            'loại thời gian',
        ],
        priority: 17,
        source: 'SOP §14 · User guide Chốt bill (form 116 / 117)',
        answer:
            '**Chốt bill:**\n' +
            '**Thêm mới** → chọn Khách hàng + **Loại thời gian** (*Thời gian chuyến* hoặc *Thời gian đơn hàng*) + khoảng ngày → hệ thống gom các chuyến **đã xác nhận đối chiếu** → **Lưu**.\n\n' +
            '• **Thêm mới không ra chuyến nào** → các chuyến chưa được **Xác nhận đối chiếu**, hoặc sai khoảng thời gian/khách hàng.\n' +
            '• Tab: Tất cả · **Bill mới tạo** · **Đã chốt bill** · **Đã xuất hóa đơn**.\n' +
            '• Chuột phải: bill mới tạo → **Chốt bill** / **Xóa**; bill đã chốt → **Cập nhật hóa đơn**.\n' +
            '• **Tổng VAT = 0** khi đơn/hợp đồng không bật **Có VAT**. Đầu kéo có thêm **Tổng LOLO**.',
    },

    // ─── Nhiên liệu ───
    {
        id: 'fuel-desktop',
        category: 'Nhiên liệu',
        questions: [
            'Quy trình cấp nhiên liệu',
            'Tạo phiếu cấp nhiên liệu',
            'Đề xuất cấp nhiên liệu',
            'Bấm Xác nhận nhưng không có gì xảy ra',
            'SL cấp khác SL đề xuất có được không',
            'Phiếu đã duyệt nhưng vẫn chờ cấp',
            'Mã phiếu FIV và PCNL khác nhau thế nào',
        ],
        keywords: [
            'cấp nhiên liệu',
            'phiếu cấp nhiên liệu',
            'đề xuất cấp',
            'sl đề xuất',
            'sl cấp',
            'trạng thái duyệt',
            'trạng thái cấp',
            'chờ cấp',
            'đã cấp',
            'trạm xăng dầu nội bộ',
        ],
        priority: 15,
        source: 'SOP §7 · User guide Danh sách cấp nhiên liệu (form 73)',
        answer:
            '**Cấp nhiên liệu:**\n' +
            'Quy trình: **Tạo đề xuất** → **Phê duyệt** → **Cập nhật cấp thực tế** → **Xác nhận cấp** → **Chốt nhiên liệu** theo kỳ.\n\n' +
            '• Hai nhóm tab độc lập: **trạng thái duyệt** (Nháp · Đang duyệt · Đã duyệt · Không duyệt · Hủy) và **trạng thái cấp** (Chờ cấp · Đã cấp · Không cấp · Đã xác nhận).\n' +
            '• Bấm **Xác nhận** không phản hồi → chưa tick chọn dòng nào.\n' +
            '• **SL cấp** được phép khác **SL đề xuất** — phần chênh sẽ thể hiện khi chốt nhiên liệu.\n' +
            '• Phiếu **Đã duyệt** nhưng vẫn **Chờ cấp** là bình thường: duyệt và cấp là hai bước riêng.\n' +
            '• Dữ liệu đã xác nhận thì không sửa được nữa.',
    },
    {
        id: 'fuel-settle',
        category: 'Nhiên liệu',
        questions: [
            'Chốt nhiên liệu',
            'SL chênh lệch âm nghĩa là gì',
            'SL tiêu hao lấy từ đâu',
            'SL định mức lấy từ đâu',
            'Khi nào dùng Tính lại',
            'Chốt nhiên liệu khác định mức nhiên liệu thế nào',
        ],
        keywords: [
            'chốt nhiên liệu',
            'sl chênh lệch',
            'sl tiêu hao',
            'sl định mức',
            'kỳ hạch toán',
            'tính lại',
        ],
        priority: 17,
        source: 'SOP §5.5 · §7.5 · User guide Chốt nhiên liệu (form 1087)',
        answer:
            '**Chốt nhiên liệu** (Quản lý tài chính › Quản lý chi phí › **Chốt nhiên liệu**, form `1087`):\n' +
            'Tổng hợp theo **xe + kỳ hạch toán**: **SL tiêu hao** (thực cấp) so với **SL định mức** (từ màn *Định mức nhiên liệu* × quãng đường) → **SL chênh lệch** × **Đơn giá** = **Số tiền**.\n\n' +
            '• **SL chênh lệch âm** = xe chạy **tiết kiệm hơn** định mức.\n' +
            '• Màn không có **Thêm mới** — dữ liệu do hệ thống tổng hợp.\n' +
            '• Chuột phải → **Tính lại** khi số liệu cấp/quãng đường có thay đổi, rồi → **Xác nhận**.',
    },
    {
        id: 'fuel-norm',
        category: 'Nhiên liệu',
        questions: [
            'Định mức nhiên liệu',
            'Khai báo định mức L/100KM',
            'Có phải nhập đủ bốn mức không',
            'Ngày áp dụng ngày kết thúc định mức',
            'Trường bắt buộc khi thêm định mức',
        ],
        keywords: [
            'định mức nhiên liệu',
            'l/100km',
            'không điều kiện',
            'không tải',
            'tải rỗng',
            'tải đầy hàng',
            'nhóm phương tiện',
            'tên định mức',
        ],
        priority: 17,
        source: 'SOP §5.4 · User guide Định mức nhiên liệu (form 1046)',
        answer:
            '**Định mức nhiên liệu** (Quản lý tài chính › Quản lý chi phí › **Định mức nhiên liệu**, form `1046`):\n' +
            'Khai báo mức tiêu hao chuẩn theo **nhóm / loại phương tiện**, đơn vị **L/100KM**, với 4 mức: **Không điều kiện**, **Không tải**, **Tải rỗng**, **Tải đầy hàng**.\n\n' +
            '• Bắt buộc: **Tên định mức** và **Nhóm phương tiện**; không cần nhập đủ cả 4 mức.\n' +
            '• **Ngày áp dụng / Ngày kết thúc** xác định khoảng hiệu lực — tránh tạo hai định mức trùng khoảng.\n' +
            '• Chuột phải dòng → **Cập nhật** / **Xóa** / **Xem**.\n' +
            'Định mức này là đầu vào cho màn **Chốt nhiên liệu**.',
    },

    // ─── Sửa chữa ───
    {
        id: 'repair',
        category: 'Sửa chữa',
        questions: [
            'Yêu cầu sửa chữa xe',
            'Quy trình sửa chữa',
            'Sửa chữa trên mobile',
            'Phiếu kẹt ở chờ duyệt CP',
            'Không thấy nút Duyệt chi phí',
            'Yêu cầu sửa lại là gì',
            'Luật duyệt CP là gì',
            'Nghiệm thu sửa chữa',
        ],
        keywords: [
            'sửa chữa',
            'phiếu sửa chữa',
            'duyệt chi phí',
            'luật duyệt cp',
            'chờ báo giá',
            'chờ nghiệm thu',
            'đang sửa chữa',
            'không duyệt cp',
            'yêu cầu sửa lại',
            'bảo dưỡng',
        ],
        priority: 15,
        source: 'SOP §21 · User guide Danh sách sửa chữa (form 36)',
        answer:
            '**Sửa chữa phương tiện:**\n' +
            'Luồng: Yêu cầu → **Chờ xác nhận** → Đánh giá kỹ thuật → **Chờ báo giá** → **Chờ duyệt CP** → **Đang sửa chữa** → **Chờ nghiệm thu** → **Hoàn thành**.\n\n' +
            '• Phiếu kẹt ở **Chờ duyệt CP** → chờ người có quyền duyệt theo **Luật duyệt CP** (nút trên toolbar cấu hình ngưỡng tự duyệt).\n' +
            '• **Không duyệt CP** → phiếu quay lại báo giá; **Yêu cầu sửa lại** dùng khi nghiệm thu không đạt.\n' +
            '• Tài xế tạo phiếu trên mobile (`/i-tms/v2/mobile-repair`) nhưng **không** duyệt/nghiệm thu được.\n' +
            '• Tab chi tiết: **Báo giá**, **Thực hiện**, **Nghiệm thu**, **Lịch sử**.',
    },

    // ─── Tạm ứng ───
    {
        id: 'advance',
        category: 'Tạm ứng',
        questions: [
            'Tạm ứng nhân sự',
            'Quy trình tạm ứng',
            'Giải ngân tạm ứng',
            'Quyết toán tạm ứng',
            'Tạo phiếu tạm ứng ở đâu',
        ],
        keywords: ['tạm ứng', 'giải ngân', 'quyết toán', 'chờ duyệt tạm ứng', 'loại tạm ứng'],
        priority: 15,
        source: 'SOP §20 · §5.8',
        answer:
            '**Tạm ứng:**\n' +
            'Luồng: Nháp → Gửi duyệt → Chờ duyệt → **Chờ giải ngân** → **Đã giải ngân** → **Quyết toán** → Hoàn thành.\n' +
            'Mã phiếu prefix **ADV**. Danh mục **Loại tạm ứng** khai báo tại **Quản lý tài chính › Quản lý chi phí › Loại tạm ứng**.',
    },

    // ─── Hợp đồng & cước ───
    {
        id: 'freight-rate',
        category: 'Cước vận chuyển',
        questions: [
            'Thiết lập bảng cước',
            'Cước vận chuyển ở đâu',
            'Biểu mẫu cước là gì',
            'Bảng cước áp dụng trống',
            'Gửi duyệt bảng cước',
            'Bảng cước đã duyệt có sửa được không',
            'Giá cước đã gồm VAT chưa',
        ],
        keywords: [
            'cước vận chuyển',
            'bảng cước',
            'biểu mẫu cước',
            'thiết lập bảng cước',
            'tham số áp dụng tính cước',
            'mã cước',
            'giá nhiên liệu',
        ],
        priority: 16,
        source: 'SOP §6 · User guide Cước vận chuyển (form 75)',
        answer:
            '**Cước vận chuyển** (Quản lý đối tác › Quản lý hợp đồng › **Cước vận chuyển**, form `75`):\n' +
            'Trình tự: chọn **Biểu mẫu cước** (cách tính) → **Thêm mới** bản cước → khai **Thông tin biểu mẫu**, **Thiết lập bảng cước**, **Tham số áp dụng tính cước** → lưu → **Bảng cước áp dụng**.\n\n' +
            '• **Bảng cước áp dụng trống** sau khi lưu → chưa nhập đủ tham số hoặc chưa chọn biểu mẫu phù hợp.\n' +
            '• Tab trạng thái: Tất cả · **Nháp** · **Đang duyệt** · **Đã duyệt**; bản **Đã duyệt** không sửa trực tiếp — tạo bản mới hoặc phụ lục.\n' +
            '• Giá nhập trên màn là giá **chưa gồm VAT** trừ khi cấu hình khác; VAT xử lý ở đơn hàng/bill.\n' +
            'Bảng cước là đầu vào cho màn **Đối chiếu**.',
    },
    {
        id: 'contract',
        category: 'Hợp đồng',
        questions: [
            'Danh sách hợp đồng',
            'Thêm hợp đồng mới',
            'Phụ lục hợp đồng ở đâu',
            'Tại sao không thấy Thêm mới trên danh sách phụ lục',
            'Gửi hợp đồng duyệt',
            'Hợp đồng đã duyệt có sửa được không',
            'Tự gia hạn hợp đồng là gì',
        ],
        keywords: [
            'hợp đồng',
            'phụ lục hợp đồng',
            'danh sách phụ lục',
            'số hợp đồng',
            'số phụ lục',
            'tự gia hạn',
            'file hợp đồng',
            'bảng giá áp dụng',
        ],
        priority: 16,
        source: 'User guide Danh sách hợp đồng (form 65) · Danh sách phụ lục (form 66)',
        answer:
            '**Hợp đồng & phụ lục** (Quản lý đối tác › Quản lý hợp đồng):\n' +
            '• **Danh sách hợp đồng** (form `65`) — **Thêm mới** → chọn **Đối tác**, Số hợp đồng, hiệu lực, upload **File hợp đồng**; toggle **Tự gia hạn**. Tab chi tiết: Thông tin hợp đồng · Thông tin phụ lục · **Bảng giá áp dụng**.\n' +
            '• **Danh sách phụ lục** (form `66`) — **không có nút Thêm mới** trên danh sách; phụ lục được tạo **từ trong hợp đồng** (tab *Thông tin phụ lục*).\n' +
            '• Bản ghi **Đã duyệt** không sửa trực tiếp; muốn thay đổi thì lập **phụ lục** mới.\n' +
            'Hợp đồng/phụ lục còn hiệu lực là điều kiện để **Đối chiếu** tính được cước.',
    },

    // ─── Danh mục nền ───
    {
        id: 'vehicle-list',
        category: 'Phương tiện',
        questions: [
            'Danh sách phương tiện',
            'Thêm xe mới',
            'Duyệt vận hành là gì',
            'Vì sao dòng xe tô màu hồng',
            'Sửa ngày hết hạn giấy phép xe',
            'Lưu và gửi duyệt khác Lưu thế nào',
            'Vì sao không nhập được mã phương tiện',
            'Điều kiện vận hành của xe',
        ],
        keywords: [
            'danh sách phương tiện',
            'phương tiện',
            'duyệt vận hành',
            'điều kiện vận hành',
            'tình trạng phương tiện',
            'lưu và gửi duyệt',
            'hồ sơ xe',
            'đăng kiểm',
        ],
        priority: 15,
        source: 'SOP §4.1 · §4.2 · User guide Danh sách phương tiện (form 33)',
        answer:
            '**Danh sách phương tiện:**\n' +
            '• **Thêm mới** → hồ sơ xe → **Lưu** (giữ ở *Nháp*) hoặc **Lưu và gửi duyệt** (chuyển *Đang duyệt*).\n' +
            '• **Mã phương tiện** do hệ thống sinh nên không sửa được.\n' +
            '• **Duyệt vận hành** (nút toolbar) khác duyệt hồ sơ: xác nhận xe **đủ điều kiện chạy** (giấy phép, đăng kiểm còn hạn).\n' +
            '• Dòng tô **màu hồng** = xe có giấy phép sắp/đã hết hạn — sửa hạn trong tab **Điều kiện vận hành** của hồ sơ xe.\n' +
            '• Tab trạng thái: Tất cả · Nháp · Đang duyệt · Đã duyệt · Không duyệt · Hủy.',
    },
    {
        id: 'employee',
        category: 'Nhân sự',
        questions: [
            'Thông tin nhân sự',
            'Thêm nhân viên mới',
            'Là tài xế bật ở đâu',
            'Sửa ngày hết hạn GPLX',
            'Nhân viên đã nghỉ việc có sửa được không',
            'Duyệt giấy phép nhân sự',
            'Loại tài xế bị mờ',
        ],
        keywords: [
            'thông tin nhân sự',
            'nhân sự',
            'nhân viên',
            'là tài xế',
            'gplx',
            'duyệt giấy phép',
            'đang làm việc',
            'đã nghỉ việc',
            'loại tài xế',
        ],
        priority: 15,
        source: 'SOP §4.1 · User guide Thông tin nhân sự (form 47)',
        answer:
            '**Thông tin nhân sự** (Quản lý nhân sự › **Thông tin nhân sự**, form `47`):\n' +
            '• Tab: **Tất cả** · **Đang làm việc** · **Đã nghỉ việc**; cột toggle **Hoạt động** và **Là tài xế** (bật ngay trên lưới).\n' +
            '• Chỉ nhân sự bật **Là tài xế** mới xuất hiện trong combobox Tài xế khi điều phối; **Loại tài xế** chỉ mở khi đã bật cờ này.\n' +
            '• Dòng **màu hồng** = giấy phép (GPLX, khám sức khỏe…) sắp/đã hết hạn — sửa trong tab **Thông tin giấy phép**.\n' +
            '• Nút toolbar: **Duyệt giấy phép**, **Đơn vị khám sức khỏe**.',
    },
    {
        id: 'partner',
        category: 'Đối tác',
        questions: [
            'Quản lý đối tác',
            'Thêm khách hàng mới',
            'Nhóm đối tác là gì',
            'Một đối tác vừa là khách hàng vừa là nhà vận chuyển được không',
            'Thêm địa điểm giao hàng cho khách',
            'Danh sách sản phẩm của khách hàng',
        ],
        keywords: [
            'đối tác',
            'nhóm đối tác',
            'khách hàng',
            'nhà vận chuyển',
            'danh sách địa điểm',
            'danh sách sản phẩm',
            'loại đối tác',
            'thêm nhanh',
        ],
        priority: 15,
        source: 'SOP §3.3 · User guide Quản lý đối tác (form 86)',
        answer:
            '**Quản lý đối tác:**\n' +
            '• Một đối tác có thể mang **nhiều loại** cùng lúc (Khách hàng + Nhà vận chuyển…) — chọn ở trường **Loại đối tác**.\n' +
            '• Nút **Nhóm đối tác** trên toolbar để gom khách theo nhóm phục vụ lọc và báo cáo.\n' +
            '• Panel chi tiết khách hàng có 6 tab: Thông tin chung · **Danh sách địa điểm** · **Danh sách sản phẩm** · Loại chứng chỉ · Phương tiện · Tài xế.\n' +
            '• Thêm điểm giao: tab **Danh sách địa điểm** → **+ Thêm mới** hoặc **+ Thêm nhanh**.\n' +
            'Địa điểm và sản phẩm khai ở đây là nguồn dữ liệu khi tạo **đơn hàng**.',
    },
    {
        id: 'setup-company',
        category: 'Thiết lập',
        questions: [
            'Thiết lập doanh nghiệp',
            'Thông tin doanh nghiệp I-TMS',
            'Thông tin giấy phép doanh nghiệp',
            'Upload logo doanh nghiệp',
            'Gửi duyệt hồ sơ doanh nghiệp',
            'Cấu hình ban đầu hệ thống',
        ],
        keywords: [
            'thông tin doanh nghiệp',
            'giấy phép doanh nghiệp',
            'thiết lập',
            'mã số thuế',
            'hình thức kinh doanh',
            'thông tin thanh toán',
        ],
        priority: 12,
        source: 'SOP §3–§5 · User guide Thông tin DN (form 18) / Giấy phép DN (form 19)',
        answer:
            '**Thiết lập nền:**\n' +
            '1) **Thông tin doanh nghiệp** (form `18`) — hồ sơ DN, logo, tab *Thông tin chung / Hình thức kinh doanh / Thông tin thanh toán*; menu **⋮** để **Gửi duyệt**.\n' +
            '2) **Thông tin giấy phép doanh nghiệp** (form `19`) — số giấy phép, loại, ngày cấp, ảnh (**Zoom Image** để xem).\n' +
            '3) **Đối tác**, **Phương tiện**, **Nhân sự** và giấy phép đi kèm.\n' +
            '4) Cấu hình tài chính: chính sách lương, loại chi phí, định mức nhiên liệu, phí cầu đường (menu **Quản lý tài chính › Quản lý chi phí**).',
    },

    // ─── Chi phí & lương (danh mục) ───
    {
        id: 'cost-type',
        category: 'Loại chi phí',
        questions: [
            'Loại chi phí là gì',
            'Thêm loại chi phí mới',
            'Định mức chi phí ở đâu',
            'Vì sao không sửa được mã chi phí',
            'Hạn mức tối đa cho một chuyến xe',
            'Tỷ lệ phần trăm tự duyệt',
        ],
        keywords: [
            'loại chi phí',
            'mã chi phí',
            'nhóm chi phí',
            'định mức chi phí',
            'hạn mức tối đa',
            'tỷ lệ tự duyệt',
            'bên chịu phí',
        ],
        priority: 15,
        source: 'SOP §5.3 · User guide Loại chi phí (form 59)',
        answer:
            '**Loại chi phí** (Quản lý tài chính › Quản lý chi phí › **Loại chi phí**, form `59`):\n' +
            'Danh mục các khoản chi phí dùng khi khai chi phí phát sinh trên chuyến.\n\n' +
            '• Trường quan trọng: **Nhóm chi phí**, **Đối tượng áp dụng**, **Hạn mức tối đa cho 1 chuyến xe**, **Tỷ lệ % tự duyệt**, **Bên chịu phí mặc định**.\n' +
            '• **Mã chi phí** không sửa được sau khi tạo.\n' +
            '• Nút **Định mức** trên toolbar mở popup **Định mức chi phí** (khác *Định mức nhiên liệu*).\n' +
            '• Bật/tắt nhanh cột **Hoạt động** ngay trên lưới, không cần mở form.',
    },
    {
        id: 'salary-policy',
        category: 'Lương',
        questions: [
            'Chính sách lương',
            'Thành phần lương ở đâu',
            'Công thức tính lương',
            'Tham số dùng làm gì',
            'Là thu nhập là khấu trừ nghĩa là gì',
            'Một chức vụ có nhiều chính sách lương không',
            'Công thức lương không chạy',
        ],
        keywords: [
            'chính sách lương',
            'thành phần lương',
            'công thức lương',
            'chức vụ áp dụng',
            'là thu nhập',
            'là khấu trừ',
            'tham số',
            'loại tính',
        ],
        priority: 16,
        source: 'SOP §5.1 · User guide Chính sách lương (form 1051)',
        answer:
            '**Chính sách lương** (Quản lý tài chính › Quản lý chi phí › **Chính sách lương**, form `1051`):\n' +
            'Khai bộ quy tắc tính lương theo **chức vụ** (tài xế đầu kéo, tài xế xe tải, giao hàng…).\n\n' +
            '• Nên tạo **Thành phần lương** trước (nút trên toolbar) — mỗi dòng *Chi tiết chính sách* chọn từ danh mục này.\n' +
            '• Toggle **Là thu nhập** / **Là khấu trừ** quyết định cộng hay trừ khi tổng hợp lương.\n' +
            '• Bảng **Tham số** tra **mã biến** dùng trong công thức (vd. `MONTHLY_TRIP_COUNT_COMPLETED`).\n' +
            '• Một chức vụ **được** có nhiều chính sách — phân biệt bằng tên và **khoảng ngày áp dụng** (tránh trùng khoảng).\n' +
            '• Công thức không chạy → kiểm tra mã tham số, điều kiện, và dữ liệu chuyến/nhân sự có đủ biến.',
    },
    {
        id: 'salary-route',
        category: 'Lương',
        questions: [
            'Lương tài xế đầu kéo theo tuyến',
            'Lương theo tuyến khác chính sách lương thế nào',
            'Container đơn container cặp là gì',
            'Khai mức lương theo tuyến đường',
        ],
        keywords: [
            'lương theo tuyến',
            'lương tài xế đầu kéo',
            'container đơn',
            'container cặp',
            'tuyến đường',
        ],
        priority: 16,
        source: 'SOP §5.2 · User guide Lương tài xế đầu kéo theo tuyến (form 1048)',
        answer:
            '**Lương tài xế đầu kéo theo tuyến** (Quản lý tài chính › Quản lý chi phí › **Lương tài xế đầu kéo**, form `1048`):\n' +
            'Khai **mức tiền cố định** theo **Tuyến đường** — cột **Container đơn** và **Container cặp**, kèm Loại vận tải và Khách hàng.\n\n' +
            '**Khác Chính sách lương:** *Chính sách lương* = công thức/thành phần theo chức vụ; màn này = **bảng giá theo tuyến**.\n' +
            'Cùng một tuyến có thể có nhiều dòng (khác khách hàng / loại vận tải).',
    },
    {
        id: 'bot-fee',
        category: 'Phí cầu đường',
        questions: [
            'Phí cầu đường',
            'Phí BOT khai ở đâu',
            'Vé tháng vé lượt',
            'Import phí cầu đường hàng loạt',
            'Nút Tiếp theo bị mờ khi import',
            'Lấy file mẫu import ở đâu',
        ],
        keywords: [
            'phí cầu đường',
            'phí bot',
            'vé tháng',
            'vé lượt',
            'import data',
            'download template',
        ],
        priority: 16,
        source: 'SOP §5.6 · §15.1 · User guide Phí cầu đường (form 1062)',
        answer:
            '**Phí cầu đường (BOT)** (Quản lý tài chính › Quản lý chi phí › **Phí cầu đường**, form `1062`):\n' +
            'Ghi nhận chi phí cầu đường theo **phương tiện + tháng**: **Vé tháng** và **Tổng tiền vé lượt** (chi tiết từng **Ngày** / **Vé lượt**).\n\n' +
            '• Nhập hàng loạt: nút **Import data** → **Download Template** → điền file Excel → upload.\n' +
            '• Nút **Tiếp theo** còn mờ nghĩa là **chưa chọn file** hợp lệ; import lỗi cột thì đối chiếu lại đúng file template.\n' +
            'Số liệu này là đầu vào cho báo cáo chi phí vận hành.',
    },
    {
        id: 'monthly-cost',
        category: 'Chi phí hàng tháng',
        questions: [
            'Chi phí hàng tháng',
            'Tại sao không có nút Thêm mới chi phí tháng',
            'Kỳ tháng mới xuất hiện khi nào',
            'Khóa kỳ chi phí tháng',
            'Mở khóa kỳ chi phí tháng',
            'Nhập khấu hao xe ở đâu',
            'Phí quản lý chung nhập ở đâu',
        ],
        keywords: [
            'chi phí hàng tháng',
            'khấu hao',
            'phí quản lý chung',
            'chi phí nhân sự',
            'đã khóa kỳ',
            'kỳ chi phí',
        ],
        priority: 16,
        source: 'SOP §5.7 · §15.2 · User guide Chi phí hàng tháng (form 1052)',
        answer:
            '**Chi phí hàng tháng** (Quản lý tài chính › Quản lý chi phí › **Chi phí hàng tháng**, form `1052`):\n' +
            'Mỗi cặp **Doanh nghiệp + Tháng** có **một** bản ghi và do **hệ thống tự sinh khi sang tháng mới** — vì vậy danh sách **không có nút Thêm mới**.\n\n' +
            '• Mở chi tiết: click link **Tháng** hoặc chuột phải → **Xem**. Bên trong có 3 khối: **Khấu hao và phí phương tiện**, **Chi phí nhân sự**, **Phí quản lý chung** — mỗi khối có **Thêm mới** riêng.\n' +
            '• Khóa kỳ bằng toggle cột **Đã khóa** trên lưới. Sau khi khóa **không thêm được** dòng nào nữa.\n' +
            '• **Chỉ mở khóa được trong vòng 24 giờ** kể từ lúc khóa; quá hạn phải theo quy trình nội bộ.',
    },

    // ─── Quy trình & trạng thái tổng ───
    {
        id: 'trip-chain',
        category: 'Quy trình',
        questions: [
            'Quy trình nghiệp vụ',
            'Quy trình nghiệp vụ SOP',
            'Chuỗi chuyến xe từ đơn đến bill',
            'Luồng nghiệp vụ xe tải',
            'Các bước làm từ đầu đến cuối',
            'Thứ tự các màn hình phải làm',
        ],
        keywords: [
            'quy trình',
            'nghiệp vụ',
            'luồng',
            'chuỗi',
            'từ đơn hàng đến bill',
            'các bước',
            'sop',
        ],
        priority: 12,
        source: 'SOP §1.4 · §2.3 · §8.5',
        answer:
            '**Chuỗi nghiệp vụ xe tải:**\n' +
            '**Đơn hàng** → **Điều phối** → Chuyến (TRP) → **Giám sát** → **Chứng từ** → **Chi phí chuyến (khóa)** → **Đối chiếu** → **Chốt bill**.\n\n' +
            'Đầu kéo chạy song song với dữ liệu FCL/container (đơn hàng đầu kéo → điều phối đầu kéo → giám sát đầu kéo → …).\n' +
            'Mỗi bước sau chỉ mở khi bước trước hoàn tất — vd. chưa **Hoàn tất CT** thì không **Khóa chi phí** được.\n' +
            'Muốn xem chi tiết từng màn → nút **Trợ giúp** trên navbar.',
    },
    {
        id: 'trip-status-layers',
        category: 'Trạng thái',
        questions: [
            'Các lớp trạng thái chuyến',
            'Trạng thái điều phối và giám sát khác gì',
            'Vì sao một chuyến có nhiều trạng thái',
            'Danh sách trạng thái trong hệ thống',
            'Tab trạng thái khác cột trạng thái thế nào',
        ],
        keywords: [
            'lớp trạng thái',
            'ma trận trạng thái',
            'trạng thái chuyến',
            'trạng thái đơn hàng',
            'chờ nhận lệnh',
            'đã nhận lệnh',
            'từ chối lệnh',
            'hủy lệnh',
        ],
        priority: 14,
        source: 'SOP §2.2 · §17',
        answer:
            '**Các lớp trạng thái** — cùng một chuyến nhưng theo dõi riêng từng lớp:\n' +
            '1) **Đơn hàng:** Nháp · Chờ tiếp nhận · Đã tiếp nhận · Từ chối · Đã tạo chuyến · Đang giao · Đã giao · Hoàn tất · Đã hủy.\n' +
            '2) **Chuyến điều phối:** Nháp · **Chờ nhận lệnh** · Đã nhận lệnh · Từ chối lệnh · Đang vận chuyển · Hoàn thành · Hủy lệnh · Thất bại.\n' +
            '3) **Giám sát:** Chờ vận chuyển · Đang vận chuyển · Chờ/Đang xử lý sự cố · Đã giao hàng · **Đã xác nhận** · Thất bại · Đã hủy.\n' +
            '4) Thêm lớp **Chứng từ** (Hoàn tất CT), **Chi phí** (Khóa), **Đối chiếu** (Đã xác nhận / Chốt bill).\n\n' +
            'Lưu ý: **tab** trên lưới thường lọc theo lớp nghiệp vụ của màn đó, còn **cột Trạng thái** hiển thị trạng thái vận hành của chuyến.',
    },

    // ─── Thao tác chung trên lưới ───
    {
        id: 'grid-common',
        category: 'Thao tác chung',
        questions: [
            'Bật hàng lọc thế nào',
            'Lọc xong không thấy đổi',
            'Ẩn hiện cột trên lưới',
            'Mất cột trên lưới',
            'Không thấy cột mong muốn',
            'Đặt lại lưới về mặc định',
            'Chuột phải không hiện menu',
            'Hiển thị 1-20 trên n kết quả nghĩa là gì',
            'Xuất Excel ở đâu',
        ],
        keywords: [
            'hàng lọc',
            'cấu hình lưới',
            'cột hiển thị',
            'lưu cấu hình',
            'đặt lại mặc định',
            'chuột phải',
            'phân trang',
            'tải lại',
            'xuất excel',
        ],
        priority: 11,
        source: 'User guide — phần Toolbar / Cấu hình lưới (chung mọi màn)',
        answer:
            '**Thao tác chung trên các màn danh sách:**\n' +
            '• **Tìm** — bật/tắt **hàng lọc** ngay dưới tiêu đề cột. Nhập điều kiện rồi **Tải lại** nếu kết quả chưa đổi.\n' +
            '• **Cột** — popup **Cột hiển thị**: tick cột cần xem → **Lưu cấu hình**; sai lệch thì **Đặt lại mặc định**.\n' +
            '• **Chuột phải** một dòng để mở menu (Xem chi tiết / Cập nhật / Xóa…) — phải click vào **ô dữ liệu**, không phải header hay vùng phân trang. Menu thay đổi theo trạng thái và quyền.\n' +
            '• *“Hiển thị 1–20 trên N kết quả”* = đang xem 20 dòng đầu trong tổng **N** bản ghi khớp bộ lọc.\n' +
            '• Không phải màn nào cũng có **Excel / Import** — tùy màn và phân quyền.',
    },
    {
        id: 'permission-missing',
        category: 'Phân quyền',
        questions: [
            'Không thấy nút Thêm mới',
            'Không thấy nút Khóa',
            'Không thấy menu bên trái',
            'Vì sao tôi thấy ít dữ liệu hơn đồng nghiệp',
            'Không vào được màn hình',
            'Bị chặn quyền',
        ],
        keywords: [
            'phân quyền',
            'không thấy nút',
            'không thấy menu',
            'ít dữ liệu hơn',
            'không vào được',
            'quyền truy cập',
            'form id',
        ],
        priority: 13,
        source: 'SOP §1.2 · User guide — mục Lỗi thường gặp (chung)',
        answer:
            '**Không thấy nút / menu / dữ liệu** — hầu hết là do **phân quyền**:\n' +
            '• Menu trái được tải từ API theo quyền tài khoản, nên mỗi người thấy danh sách khác nhau.\n' +
            '• Nút trên toolbar (Thêm mới, Khóa, Xác nhận, Duyệt…) ẩn/hiện theo quyền trên **Form ID** của màn (số cuối trong URL).\n' +
            '• Thấy ít bản ghi hơn đồng nghiệp thường do **phạm vi doanh nghiệp** gán cho tài khoản, hoặc do bộ lọc đang bật.\n' +
            'Cách xử lý: **Xóa bộ lọc** + **Tải lại** trước; nếu vẫn thiếu, liên hệ quản trị hệ thống kèm **Form ID** và thao tác đang cần.',
    },
    {
        id: 'approval-flow',
        category: 'Phê duyệt',
        questions: [
            'Nháp đang duyệt đã duyệt nghĩa là gì',
            'Gửi duyệt hồ sơ',
            'Gửi duyệt ở đâu',
            'Hồ sơ không duyệt xử lý thế nào',
            'Bản ghi đã duyệt có sửa được không',
            'Tab Nháp hiển thị 0 có lỗi không',
        ],
        keywords: [
            'gửi duyệt',
            'đang duyệt',
            'đã duyệt',
            'không duyệt',
            'trạng thái phê duyệt',
            'phê duyệt',
        ],
        priority: 13,
        source: 'User guide — luồng duyệt chung (hợp đồng, cước, phương tiện, DN)',
        answer:
            '**Luồng duyệt hồ sơ** (dùng chung cho hợp đồng, cước, phương tiện, doanh nghiệp, nhiên liệu…):\n' +
            '**Nháp** → **Đang duyệt** → **Đã duyệt** (hoặc **Không duyệt** / **Hủy**).\n\n' +
            '• Gửi duyệt: nút **Lưu và gửi duyệt** trong form, hoặc menu **⋮** / chuột phải → **Gửi duyệt** trên bản ghi Nháp.\n' +
            '• Bản ghi **Đã duyệt** thường **không sửa trực tiếp** — tạo bản mới hoặc phụ lục.\n' +
            '• **Không duyệt** → bản ghi quay lại để chỉnh sửa rồi gửi duyệt lại.\n' +
            '• Tab **Nháp (0)** không phải lỗi — chỉ nghĩa là hiện không có bản ghi nào ở trạng thái đó.',
    },
];

/** Danh mục knowledge (cho mở rộng UI sau này) */
export const AI_ASSISTANT_KNOWLEDGE_CATEGORIES = [
    'Đăng nhập',
    'Trợ giúp',
    'Thuật ngữ',
    'Báo cáo',
    'Bản đồ đội xe',
    'Đơn hàng',
    'Điều phối tải',
    'Điều phối đầu kéo',
    'Điều phối',
    'Chuyến xe',
    'Giám sát',
    'Giám sát đầu kéo',
    'Mobile',
    'Chứng từ',
    'Chi phí chuyến',
    'Đối chiếu',
    'Chốt bill',
    'Nhiên liệu',
    'Sửa chữa',
    'Tạm ứng',
    'Cước vận chuyển',
    'Hợp đồng',
    'Phương tiện',
    'Nhân sự',
    'Đối tác',
    'Thiết lập',
    'Loại chi phí',
    'Lương',
    'Phí cầu đường',
    'Chi phí hàng tháng',
    'Quy trình',
    'Trạng thái',
    'Thao tác chung',
    'Phân quyền',
    'Phê duyệt',
] as const;
